// frontend/src/pages/reports/IncomeStatement.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./IncomeStatement.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) currency = company.currency.code;
    else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) currency = company.currency;
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) currency = company.currency_code;
    else if (typeof company.currency_code === "object" && company.currency_code.code) currency = company.currency_code.code;
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) currency = company.base_currency;
    else if (typeof company.base_currency === "object" && company.base_currency.code) currency = company.base_currency.code;
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) currency = "USD";
  return currency;
};

export default function IncomeStatement() {
  const { company: authCompany } = useAuth();

  // ✅ NEW: full company with address/contact/details
  const [fullCompany, setFullCompany] = useState(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));

  // ✅ NEW: load the full company once (has address, phones, email)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${url}/api/company/current`, {
          headers: getAuthHeaders(),
          timeout: 5000,
        });
        if (!cancelled && res?.data) {
          setFullCompany(res.data);
        }
      } catch {
        // Fallback to auth company on failure
        if (!cancelled) setFullCompany(authCompany || null);
      }
    })();
    return () => { cancelled = true; };
  }, [authCompany]);

  // ✅ Use the full company (falls back to auth company while loading)
  const company = fullCompany || authCompany;

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const formatCurrency = useCallback(
    (amount) => {
      const v = Number(amount || 0);
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: companyCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(v);
      } catch {
        return `${companyCurrency} ${v.toFixed(2)}`;
      }
    },
    [companyCurrency]
  );

  const signedMoney = useCallback(
    (value) => {
      const amount = Number(value || 0);
      return amount < 0 ? `(${formatCurrency(Math.abs(amount))})` : formatCurrency(amount);
    },
    [formatCurrency]
  );

  const loadIncomeStatement = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (asOfDate) params.as_of_date = asOfDate;

      const res = await axios.get(`${url}/api/reports/income-statement`, {
        headers: getAuthHeaders(),
        params,
      });
      setData(res.data.data || res.data);
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    loadIncomeStatement();
  }, [loadIncomeStatement]);

  const handlePrint = () => window.print();

  // ==================================================
  // Data extraction
  // ==================================================
  const revenueAccounts = data?.revenue?.accounts || [];
  const salesDiscountAccounts = data?.sales_discount?.accounts || [];
  const cogsAccounts = data?.cost_of_goods_sold?.accounts || [];
  const operatingExpensesAccounts = data?.operating_expenses?.accounts || [];

  const totalRevenue = data?.revenue?.total ?? 0;
  const totalSalesDiscount = data?.sales_discount?.total ?? 0;
  const netSales = data?.net_sales ?? 0;
  const totalCOGS = data?.cost_of_goods_sold?.total ?? 0;
  const grossProfit = data?.gross_profit ?? 0;
  const totalOperatingExpenses = data?.operating_expenses?.total ?? 0;
  const netProfit = data?.net_profit_loss ?? 0;

  const displayDate = data?.as_of_date
    ? new Date(data.as_of_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const logoUrl = company?.logo
    ? company.logo.startsWith("http")
      ? company.logo
      : `${url}/${company.logo.replace(/^\/+/, "")}`
    : null;

  // ✅ Build phone string (1, 2, 3 joined by ·)
  const phoneLine = [
    company?.contact,
    company?.contact_2,
    company?.contact_3,
  ].filter(Boolean).join(" · ");

  return (
    <div className="income-page">
      {/* ==================================================
          SCREEN CONTROLS
      ================================================== */}
      <div className="screen-only income-container">
        {/* Title block */}
        <div className="income-title-block">
          <h1 className="income-title-main">INCOME STATEMENT</h1>
          <div className="income-title-company">
            {company?.company_name || "Company Name"}
          </div>
          <div className="income-title-asof">
            For the Period Ended {displayDate}
          </div>
        </div>

        {/* Filter bar */}
        <div className="income-filter">
          <div className="filter-group">
            <label htmlFor="is-date">As of Date</label>
            <input
              id="is-date"
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </div>

          <button
            onClick={loadIncomeStatement}
            disabled={loading}
            style={{
              padding: "10px 18px",
              background: "#170f48",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              minWidth: 120,
              opacity: loading ? 0.6 : 1,
            }}
          >
            ↻ {loading ? "Refreshing..." : "Refresh"}
          </button>

          <button
            onClick={handlePrint}
            disabled={loading}
            style={{
              padding: "10px 18px",
              background: "#f1592a",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              minWidth: 140,
              opacity: loading ? 0.6 : 1,
            }}
          >
            🖨 Print (A4)
          </button>

          <span className="filter-info">
            {loading ? "Loading..." : `Showing data up to ${displayDate}`}
          </span>
        </div>

        {/* Status badge */}
        <div className={netProfit >= 0 ? "profit-status" : "loss-status"}>
          <span>{netProfit >= 0 ? "✓" : "!"}</span>
          {netProfit >= 0 ? "Net Profit" : "Net Loss"}
        </div>

        {/* Summary cards */}
        <div className="income-summary">
          <div className="summary-box revenue">
            <small>Total Revenue</small>
            <strong>{formatCurrency(totalRevenue)}</strong>
          </div>
          <div className="summary-box gross">
            <small>Gross Profit</small>
            <strong>{formatCurrency(grossProfit)}</strong>
          </div>
          <div className="summary-box expense">
            <small>Operating Expenses</small>
            <strong>{formatCurrency(totalOperatingExpenses)}</strong>
          </div>
          <div className={netProfit >= 0 ? "summary-box profit" : "summary-box loss"}>
            <small>Net Profit / Loss</small>
            <strong>{signedMoney(netProfit)}</strong>
          </div>
        </div>

        {/* Statement card */}
        <div className="statement-card">
          <div className="statement-title">
            <h2>INCOME STATEMENT</h2>
            <span>Period Ended {displayDate}</span>
          </div>

          {loading ? (
            <div style={{ padding: "40px", textAlign: "center" }}>Loading...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th className="amount">Amount ({companyCurrency})</th>
                </tr>
              </thead>
              <tbody>
                {/* REVENUE */}
                {revenueAccounts.length === 0 ? (
                  <tr>
                    <td>No revenue accounts</td>
                    <td className="amount">{formatCurrency(0)}</td>
                  </tr>
                ) : (
                  revenueAccounts.map((item, index) => (
                    <tr key={`revenue-${index}`}>
                      <td>{item.account_name}</td>
                      <td className="amount income">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))
                )}

                {/* SALES DISCOUNT */}
                {salesDiscountAccounts.map((item, index) => (
                  <tr key={`discount-${index}`}>
                    <td className="negative">Less: {item.account_name}</td>
                    <td className="amount negative">({formatCurrency(item.amount)})</td>
                  </tr>
                ))}

                {/* Net Sales */}
                <tr className="total-row net-sales-row">
                  <td>Net Sales</td>
                  <td className="amount">{signedMoney(netSales)}</td>
                </tr>

                {/* COGS */}
                {cogsAccounts.length === 0 ? (
                  <tr>
                    <td>Less: Cost of Goods Sold</td>
                    <td className="amount negative">{formatCurrency(0)}</td>
                  </tr>
                ) : (
                  cogsAccounts.map((item, index) => (
                    <tr key={`cogs-${index}`}>
                      <td>Less: {item.account_name}</td>
                      <td className="amount negative">({formatCurrency(item.amount)})</td>
                    </tr>
                  ))
                )}

                {/* Gross Profit */}
                <tr className="total-row gross-row">
                  <td>Gross Profit / (Loss)</td>
                  <td className="amount">{signedMoney(grossProfit)}</td>
                </tr>

                {/* Operating Expenses */}
                <tr className="section">
                  <td colSpan="2">Operating Expenses</td>
                </tr>

                {operatingExpensesAccounts.length > 0 ? (
                  operatingExpensesAccounts.map((item, index) => (
                    <tr key={`expense-${index}`}>
                      <td className="indent">{item.account_name}</td>
                      <td className="amount">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="indent">No operating expenses</td>
                    <td className="amount">{formatCurrency(0)}</td>
                  </tr>
                )}

                <tr className="subtotal">
                  <td>Total Operating Expenses</td>
                  <td className="amount">{formatCurrency(totalOperatingExpenses)}</td>
                </tr>

                <tr className="grand-total">
                  <td>Net Profit / (Loss) for the Period</td>
                  <td className="amount">{signedMoney(netProfit)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Net profit/loss check */}
        {!loading && (
          <div className={`income-check ${netProfit >= 0 ? "profit-ok" : "profit-bad"}`}>
            <div className="income-check-icon">{netProfit >= 0 ? "✓" : "!"}</div>
            <div className="income-check-body">
              <strong className="income-check-title">
                {netProfit >= 0 ? "Net Profit Explanation" : "Net Loss Explanation"}
              </strong>
              <div className="income-check-formula">
                <span>Net Profit (Loss)</span>
                <span className="income-check-eq">=</span>
                <span>Net Sales</span>
                <span className="income-check-eq">−</span>
                <span>Cost of Goods Sold</span>
                <span className="income-check-eq">−</span>
                <span>Operating Expenses</span>
              </div>
              <div className="income-check-values">
                <span>{formatCurrency(netSales)}</span>
                <span>−</span>
                <span>{formatCurrency(totalCOGS)}</span>
                <span>−</span>
                <span>{formatCurrency(totalOperatingExpenses)}</span>
                <span className="income-check-eq">=</span>
                <strong>{signedMoney(netProfit)}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Key point */}
        {!loading && (
          <div className="income-key-point">
            <span className="income-key-icon" aria-hidden="true">💡</span>
            <p className="income-key-text">
              <strong>Key Point:</strong> If total expenses are greater than
              net sales, the result is a Net Loss (shown in brackets).
            </p>
          </div>
        )}
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && <img src={logoUrl} alt="Logo" className="print-logo" />}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || "Company Name"}
              </h1>

              {/* ✅ Address now comes from /api/company/current */}
              <p className="print-company-line">
                {company?.address || "No address"}
              </p>

              {/* ✅ Phone(s) — Phone 1 · Phone 2 · Phone 3 */}
              {phoneLine && (
                <p className="print-company-line">
                  Tel: {phoneLine}
                </p>
              )}

              <p className="print-company-line">
                {company?.admin_email ? `Email: ${company.admin_email}` : ""}
                {company?.company_code ? ` · Code: ${company.company_code}` : ""}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{displayDate}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">
                {fiscalYearLabel}-{fiscalYearLabel + 1}
              </span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">INCOME STATEMENT</h2>
          <p className="print-report-subtitle">
            For the Period Ended {displayDate}
          </p>
        </div>

        {/* Statement table */}
        <table className="print-is-table">
          <thead>
            <tr>
              <th className="print-is-particulars">Particulars</th>
              <th className="print-is-amount">Amount ({companyCurrency})</th>
            </tr>
          </thead>
          <tbody>
            {/* Revenue */}
            <tr className="print-is-section-row">
              <td colSpan="2">Revenue</td>
            </tr>
            {revenueAccounts.length === 0 ? (
              <tr>
                <td className="print-is-label-indent">No revenue accounts</td>
                <td className="print-is-amount">{formatCurrency(0)}</td>
              </tr>
            ) : (
              revenueAccounts.map((item, i) => (
                <tr key={`pr-${i}`}>
                  <td className="print-is-label-indent">{item.account_name}</td>
                  <td className="print-is-amount">{formatCurrency(item.amount)}</td>
                </tr>
              ))
            )}
            <tr className="print-is-subtotal">
              <td>Total Revenue</td>
              <td className="print-is-amount">{formatCurrency(totalRevenue)}</td>
            </tr>

            {/* Sales Discount */}
            {salesDiscountAccounts.length > 0 && (
              <>
                {salesDiscountAccounts.map((item, i) => (
                  <tr key={`pd-${i}`}>
                    <td className="print-is-label-indent negative">Less: {item.account_name}</td>
                    <td className="print-is-amount negative">({formatCurrency(item.amount)})</td>
                  </tr>
                ))}
              </>
            )}

            <tr className="print-is-subtotal">
              <td>Net Sales</td>
              <td className="print-is-amount">{signedMoney(netSales)}</td>
            </tr>

            {/* COGS */}
            <tr className="print-is-section-row">
              <td colSpan="2">Cost of Goods Sold</td>
            </tr>
            {cogsAccounts.length === 0 ? (
              <tr>
                <td className="print-is-label-indent">No COGS</td>
                <td className="print-is-amount">{formatCurrency(0)}</td>
              </tr>
            ) : (
              cogsAccounts.map((item, i) => (
                <tr key={`pc-${i}`}>
                  <td className="print-is-label-indent">{item.account_name}</td>
                  <td className="print-is-amount">({formatCurrency(item.amount)})</td>
                </tr>
              ))
            )}
            <tr className="print-is-subtotal">
              <td>Total Cost of Goods Sold</td>
              <td className="print-is-amount">({formatCurrency(totalCOGS)})</td>
            </tr>

            {/* Gross Profit */}
            <tr className="print-is-emphasis">
              <td>Gross Profit / (Loss)</td>
              <td className="print-is-amount">{signedMoney(grossProfit)}</td>
            </tr>

            {/* Operating Expenses */}
            <tr className="print-is-section-row">
              <td colSpan="2">Operating Expenses</td>
            </tr>
            {operatingExpensesAccounts.length === 0 ? (
              <tr>
                <td className="print-is-label-indent">No operating expenses</td>
                <td className="print-is-amount">{formatCurrency(0)}</td>
              </tr>
            ) : (
              operatingExpensesAccounts.map((item, i) => (
                <tr key={`po-${i}`}>
                  <td className="print-is-label-indent">{item.account_name}</td>
                  <td className="print-is-amount">{formatCurrency(item.amount)}</td>
                </tr>
              ))
            )}
            <tr className="print-is-subtotal">
              <td>Total Operating Expenses</td>
              <td className="print-is-amount">{formatCurrency(totalOperatingExpenses)}</td>
            </tr>

            {/* Net Profit / Loss */}
            <tr className="print-is-grand-total">
              <td>Net Profit / (Loss) for the Period</td>
              <td className="print-is-amount">{signedMoney(netProfit)}</td>
            </tr>
          </tbody>
        </table>

        {/* Status */}
        <div className="print-status">
          {netProfit >= 0 ? (
            <p className="print-status-ok">
              ✓ Net Profit for the period: {formatCurrency(netProfit)}
            </p>
          ) : (
            <p className="print-status-bad">
              ! Net Loss for the period: {formatCurrency(Math.abs(netProfit))}
            </p>
          )}
        </div>

        {/* Signature */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ==================================================
          INLINE PRINT STYLES
      ================================================== */}
      <style>{`
        .print-only { display: none; }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }
          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }
          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }
          .screen-only { display: none !important; }

          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 4mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 5mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }

          .print-is-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9.5pt;
          }
          .print-is-table thead th {
            background: #170f48 !important;
            color: #ffffff !important;
            font-weight: 700;
            text-align: left;
            padding: 3mm 3mm;
            font-size: 9pt;
            letter-spacing: 0.5pt;
            text-transform: uppercase;
            border: 0.5pt solid #170f48;
          }
          .print-is-table thead th.print-is-amount {
            text-align: right;
          }
          .print-is-table td {
            padding: 2mm 3mm;
            border-bottom: 0.3pt solid #e0e4ea;
            font-size: 9.5pt;
          }
          .print-is-table td.print-is-amount {
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }
          .print-is-table td.print-is-label-indent {
            padding-left: 8mm;
          }
          .print-is-table td.negative { color: #b91c1c; }
          .print-is-table .print-is-section-row td {
            background: #f0f2f5 !important;
            font-weight: 700;
            color: #170f48;
            font-size: 9pt;
            text-transform: uppercase;
            letter-spacing: 0.4pt;
            padding-top: 2.5mm;
            padding-bottom: 2.5mm;
            border-top: 0.5pt solid #ccc;
          }
          .print-is-table .print-is-subtotal td {
            background: #f8fafc !important;
            font-weight: 700;
            border-top: 0.5pt solid #999;
            border-bottom: 0.5pt solid #999;
            padding-top: 2.5mm;
            padding-bottom: 2.5mm;
          }
          .print-is-table .print-is-emphasis td {
            background: #eff6ff !important;
            font-weight: 800;
            color: #170f48;
            font-size: 10pt;
            padding-top: 3mm;
            padding-bottom: 3mm;
            border-top: 1pt solid #170f48;
            border-bottom: 1pt solid #170f48;
          }
          .print-is-table .print-is-grand-total td {
            background: #170f48 !important;
            color: #ffffff !important;
            font-weight: 800;
            font-size: 10.5pt;
            letter-spacing: 0.4pt;
            padding-top: 3.5mm;
            padding-bottom: 3.5mm;
            border-top: 1pt solid #170f48;
          }

          .print-status { margin-top: 5mm; text-align: center; font-size: 9pt; }
          .print-status-ok { color: #1e7e34; font-weight: 700; margin: 0; }
          .print-status-bad { color: #c82333; font-weight: 700; margin: 0; }

          .print-footer { margin-top: 10mm; page-break-inside: avoid; }
          .print-footer-row { display: flex; justify-content: space-between; gap: 8mm; margin-bottom: 5mm; }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
}
